import { Position, PathSegment, MazeState } from './types/maze-types';
import { logger } from '@/shared/infrastructure/logging/logger';

/**
 * Менеджер состояния лабиринта - адаптирован для новой архитектуры
 */
export class MazeStateManager {
  private state: MazeState;
  private listeners: Set<() => void> = new Set();

  constructor(initialState: Partial<MazeState> = {}) {
    this.state = {
      maze: [],
      rows: 0,
      cols: 0,
      cellSize: 20,
      canvasSize: { width: 800, height: 600 },
      start: { x: 0, y: 0 },
      end: { x: 0, y: 0 },
      entranceZone: null,
      exitZone: null,
      pathSegments: [],
      isCompleted: false,
      ...initialState
    };
  }

  // Получение состояния
  getState(): MazeState {
    return { ...this.state };
  }

  // Обновление состояния
  updateState(updates: Partial<MazeState>): void {
    this.state = { ...this.state, ...updates };
    this.notifyListeners();
  }

  // Подписка на изменения
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  // Предзагрузка изображений
  async preloadImages(imageUrls: string[]): Promise<void> {
    const loadPromises = imageUrls.map(async (url) => {
      try {
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = reject;
          img.src = url.startsWith('/') ? `${window.location.origin}${url}` : url;
        });
      } catch (error) {
        logger.warn(`Failed to load image: ${url}`, error);
      }
    });

    await Promise.all(loadPromises);
    this.notifyListeners();
  }

  // Очистка пути
  clearPath(): void {
    this.updateState({ 
      pathSegments: [],
      isCompleted: false 
    });
  }

  // Добавление сегмента пути
  addPathSegment(segment: PathSegment): void {
    this.updateState({
      pathSegments: [...this.state.pathSegments, segment]
    });
  }

  // Обновление последнего сегмента пути
  updateLastPathSegment(points: Position[]): void {
    const segments = [...this.state.pathSegments];
    if (segments.length > 0) {
      segments[segments.length - 1].points = points;
      this.updateState({ pathSegments: segments });
    }
  }
}
